import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import { ArrowBack } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Typography } from '@mui/material'

export interface DetailPageMessageCardProps {
  title: string
  description: React.ReactNode
  // Target of the back button above the card, e.g. `/live-event-analytics${rootCrumbSearch}`.
  backTo: string
  // Defaults to "Back to catalog".
  backLabel?: string
  // Shows a retry button under the description when set.
  onRetry?: () => void
  // Defaults to "Try again".
  retryLabel?: string
  className?: string
}

/**
 * Full-page message for the Live Event Analytics detail pages when there is
 * nothing to render: an unknown id (not found) or a failed load (pass
 * `onRetry`). The back navigation stays in place so the user is never stuck.
 */
export const DetailPageMessageCard: React.FC<DetailPageMessageCardProps> = ({ title, description, backTo, backLabel, onRetry, retryLabel, className }) => {
  const { t } = useTranslation()

  return (
    <Box className={className}>
      <Box sx={{ mb: 2 }}>
        <Button component={RouterLink} to={backTo} startIcon={<ArrowBack />} variant="text" color="primary">
          {backLabel ?? t('market-analytics:back_to_catalog')}
        </Button>
      </Box>
      <Card>
        <CardContent role={onRetry ? 'alert' : undefined} sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h3" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: onRetry ? 3 : 0 }}>
            {description}
          </Typography>
          {onRetry ? (
            <Button variant="contained" color="primary" onClick={onRetry}>
              {retryLabel ?? t('market-analytics:retry')}
            </Button>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  )
}
